// /sports/super-admin/review — every stop, void, outage, override, forced
// end, failed command and double-settlement conflict across the fleet, in
// time order (K6 Phase 2). "Needs attention" is the product: with a hundred
// machines nobody reads every row. The per-device scorecard sits at the
// bottom (stops %, free stops, mistakes/day, outages/day, negative court
// share, and the 15-minute "ran fine after a problem" rule).
import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ShieldAlert, RefreshCw, AlertTriangle, AlertOctagon, Terminal } from 'lucide-react';
import { toast } from 'sonner';
import { SuperAdminShell } from './SportsSuperAdminPage';
import { PeriodPicker } from './SportsKioskSalesPage';
import { getReview, getScorecard, listDevices, isAdminDenied, errMsg, ymd, shiftYmd, epochDayToYmd } from '../kiosk/kioskApi';
import { RED, GREEN, GREY, AMBER, AdminDeniedNotice, fmtCentavos, fmtMs, labelStyle, inputStyle } from '../kiosk/KioskDialogs';
import { STOP_LABEL } from '../kiosk/KioskLive';
import { bonusFreeLabel } from '../kiosk/KioskVoucherWizard';
import { fmtHrMin, fmtBookedPlayed } from '../liveUi';
import { useAutoRefresh } from '../kiosk/useAutoRefresh';

const FILTERS = [
  ['attention', 'Needs attention'], ['all', 'Everything'], ['late', 'Late stops'], ['stops', 'Stops'], ['voids', 'Not charged'],
  ['outages', 'Outages'], ['overrides', 'Overrides'], ['forced', 'Forced ends'], ['conflicts', 'Conflicts'], ['commands', 'Failed commands'],
];
const REASON_LABEL = { override: 'Override', forced_end: 'Forced end', outage: 'Outage', court_comp: 'Court comp', late_stop: 'Late stop', conflict: 'Double settlement', failed_command: 'Command failed' };
const card = { background: '#141414', border: '1px solid #1E1E1E', borderRadius: 12, padding: 16 };

function matches(item, filter) {
  if (filter === 'all') return true;
  if (filter === 'attention') return item.needs_attention;
  if (filter === 'conflicts') return item.kind === 'conflict';
  if (filter === 'commands') return item.kind === 'command';
  if (item.kind !== 'session') return false;
  const f = item.flags;
  return { stops: f.is_stop, voids: f.is_void, outages: f.is_outage, overrides: f.is_override, forced: f.is_forced_end, late: f.is_late_stop }[filter];
}

function Chip({ text, color = GREY, testId }) {
  return (
    <span data-testid={testId} style={{ fontSize: 10, fontWeight: 800, textTransform: 'uppercase', letterSpacing: 0.5,
      color, background: color + '18', padding: '2px 7px', borderRadius: 999 }}>{text}</span>
  );
}

function Item({ it }) {
  const red = it.kind === 'conflict' || it.attention_reasons.includes('outage') || it.attention_reasons.includes('failed_command');
  const border = it.kind === 'conflict' ? '#FF2D55' : it.needs_attention ? AMBER + '80' : '#1E1E1E';
  const Icon = it.kind === 'conflict' ? AlertOctagon : it.kind === 'command' ? Terminal : AlertTriangle;
  return (
    <div data-testid={`kiosk-review-item-${it.kind}-${it.row_id || it.command_id}`}
      style={{ ...card, borderColor: border, marginBottom: 8, display: 'flex', gap: 12, alignItems: 'flex-start' }}>
      <Icon className="h-4 w-4 shrink-0 mt-1" style={{ color: red ? '#FF2D55' : it.needs_attention ? AMBER : '#555' }} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div className="flex items-center gap-2 flex-wrap">
          <span style={{ fontSize: 12, color: GREY }}>{fmtMs(it.at_ms)}</span>
          <Link to={`/sports/super-admin/devices/${it.kiosk_id}`} data-testid={`kiosk-review-device-${it.kiosk_id}`}
            style={{ fontFamily: 'Oswald, sans-serif', fontSize: 14, fontWeight: 700, color: '#fff', textDecoration: 'none' }}>{it.device_name}</Link>
          {it.site_name && <span style={{ fontSize: 12, color: GREY }}>{it.site_name}</span>}
          {it.court_name && <span style={{ fontSize: 11, color: '#555' }}>· {it.court_name}</span>}
          {it.attention_reasons.map(r => <Chip key={r} text={REASON_LABEL[r] || r} color={r === 'conflict' || r === 'outage' || r === 'failed_command' ? '#FF2D55' : AMBER} testId={`kiosk-review-reason-${r}`} />)}
        </div>
        {it.kind === 'session' && (
          <p style={{ fontSize: 13, marginTop: 4 }}>
            {/* 2026-09-30: who played (asked before the rental), and a free trial named as one. */}
            {it.customer_name && <span style={{ color: '#fff', fontWeight: 700 }} data-testid={`kiosk-review-name-${it.row_id}`}>{it.customer_name} · </span>}
            {it.trial && <span style={{ color: GREEN, fontWeight: 700 }} data-testid={`kiosk-review-trial-${it.row_id}`}>Free trial · </span>}
            <span style={{ color: it.stop_reason ? AMBER : '#ccc', fontWeight: 600 }}>{it.stop_reason ? (STOP_LABEL[it.stop_reason] || it.stop_reason) : (it.status === 'VOID' ? 'Not charged' : it.end_reason === 'REMOTE' ? 'Forced end from the web' : `Ended by ${(it.end_reason || '').toLowerCase()}`)}</span>
            {/* T1: "2 hrs" / "2 hrs booked · 1 min played" / "open · 45 min"; the old figure until the server sends both. */}
            <span style={{ color: GREY }}> · {fmtBookedPlayed(it.booked_minutes, it.played_minutes, it.mode) ?? fmtHrMin(it.billed_minutes ?? it.minutes)} · charged<b style={{ color: '#fff' }}>{fmtCentavos(it.charged_centavos)}</b>
              {it.notional_centavos != null && it.notional_centavos !== it.charged_centavos ? ` (would have been ${fmtCentavos(it.notional_centavos)})` : ''}
              {it.bearer && it.bearer !== 'NOBODY' ? ` · borne by ${it.bearer.toLowerCase()}` : ''}
              {it.authorised_by ? ` · ${it.authorised_by}` : ''}
              {it.flags?.is_late_stop ? <span style={{ color: AMBER }}> · late stop: {fmtCentavos(it.late_stop_forgone_centavos)} not charged</span> : ''}
            </span>
            {/* K6 P4b — the voucher on the row and the court booking it was claimed on (court_booking_ref, joined by the server). */}
            {it.voucher?.code && <span style={{ color: '#4FC3F7' }}> · voucher {it.voucher.code}</span>}
            {/* K6 P4c — free machine time on the row (voucher_bonus_minutes); nothing was deducted, the clock simply ran longer. */}
            {bonusFreeLabel(it.voucher_bonus_minutes) && <span style={{ color: GREEN }} data-testid={`kiosk-review-bonus-${it.row_id}`}> · {bonusFreeLabel(it.voucher_bonus_minutes)}</span>}
            {it.court_booking_ref && <span style={{ color: AMBER }} data-testid={`kiosk-review-court-booking-${it.row_id}`}> · court booking {it.court_booking_ref}</span>}
            {it.derived && <span style={{ color: GREY }}> · court side <b style={{ color: it.court_side_centavos < 0 ? AMBER : GREEN }}>{fmtCentavos(it.court_side_centavos)}</b> (derived)</span>}
            {it.collected && <span style={{ color: '#555' }}> · settled</span>}
          </p>
        )}
        {it.kind === 'conflict' && (
          <p style={{ fontSize: 13, marginTop: 4, color: '#FF2D55' }}>
            The phone settled {it.conflict_detail?.session_row_ids?.length || 0} session(s) already collected on the web
            ({(it.conflict_detail?.web_collection_ids || []).join(', ')}) — phone collection #{it.row_id}, {epochDayToYmd(it.period_from_epoch_day)} to {epochDayToYmd(it.period_to_epoch_day)},
            gross {fmtCentavos(it.gross_centavos)}. Kept verbatim, never merged. Settle the difference by hand.
          </p>
        )}
        {it.kind === 'command' && (
          <p style={{ fontSize: 13, marginTop: 4, color: '#ccc' }}>{it.code} failed{it.error ? `: ${it.error}` : ''}{it.issued_by_name ? ` · issued by ${it.issued_by_name}` : ''}</p>
        )}
        {it.note && <p style={{ fontSize: 12, color: GREY, marginTop: 2 }}>{it.note}</p>}
      </div>
    </div>
  );
}

function Scorecard({ card: c }) {
  const bad = c.restart_flag_count > 0 || c.negative_court_share || c.outlier || c.late_stops > 0;
  const R = { padding: '10px 12px', fontSize: 13, textAlign: 'right' };
  return (
    <tr data-testid={`kiosk-scorecard-${c.kiosk_id}`} style={{ borderBottom: '1px solid #1E1E1E', background: bad ? 'rgba(255,45,85,0.05)' : 'transparent' }}>
      <td style={{ padding: '10px 12px' }}>
        <Link to={`/sports/super-admin/devices/${c.kiosk_id}`} style={{ fontFamily: 'Oswald, sans-serif', fontSize: 14, fontWeight: 700, color: '#fff', textDecoration: 'none' }}>{c.name}</Link>
        <div style={{ fontSize: 11, color: GREY }}>{[c.site_name, c.court_name].filter(Boolean).join(' · ')}{c.status !== 'active' ? ' · revoked' : ''}</div>
      </td>
      <td style={R}>{c.sessions}</td>
      <td style={{ ...R, color: c.outlier ? '#FF2D55' : undefined }}>{c.stop_pct}%{c.outlier ? ' ⚠' : ''}</td>
      <td style={R}>{c.free_stops}</td>
      <td style={R}>{c.mistakes_per_day}</td>
      <td style={{ ...R, color: c.outages ? AMBER : undefined }}>{c.outages_per_day}</td>
      <td style={{ ...R, color: c.late_stops ? AMBER : GREY, fontWeight: c.late_stops ? 800 : 400 }} title={c.late_stops ? `${fmtCentavos(c.late_stop_forgone_centavos)} not charged` : ''}>
        {c.late_stops || 0}{c.late_stops ? <span style={{ fontSize: 11, fontWeight: 400, color: GREY }}> · {fmtCentavos(c.late_stop_forgone_centavos)}</span> : ''}
      </td>
      <td style={{ ...R, color: c.negative_court_share ? '#FF2D55' : GREEN }}>{fmtCentavos(c.court_share_centavos)}</td>
      <td style={{ ...R, color: c.restart_flag_count ? '#FF2D55' : GREY, fontWeight: c.restart_flag_count ? 800 : 400 }}>{c.restart_flag_count}</td>
    </tr>
  );
}

export default function SportsKioskReviewPage() {
  const [[from, to], setRange] = useState(() => [shiftYmd(ymd(), -6), ymd()]);
  const [kioskId, setKioskId] = useState('');
  const [filter, setFilter] = useState('attention');
  const [devices, setDevices] = useState([]);
  const [data, setData] = useState(null);
  const [score, setScore] = useState(null);
  const [loading, setLoading] = useState(true);
  const [denied, setDenied] = useState(false);

  const load = useCallback((silent = false) => {
    if (!silent) setLoading(true);
    return Promise.all([getReview({ from, to, kiosk_id: kioskId || undefined }), getScorecard({ from, to })])
      .then(([r, s]) => { setData(r); setScore(s); setDenied(false); })
      .catch(e => { if (isAdminDenied(e)) setDenied(true); else if (!silent) toast.error(errMsg(e, 'Could not load the review')); })
      .finally(() => setLoading(false));
  }, [from, to, kioskId]);
  useEffect(() => { load(); }, [load]);
  useAutoRefresh(() => load(true));
  useEffect(() => { listDevices().then(d => setDevices(Array.isArray(d) ? d : [])).catch(() => {}); }, []);

  const items = (data?.items || []).filter(i => matches(i, filter));
  const counts = data?.counts;
  const countOf = { attention: counts?.needs_attention, all: counts?.total, late: counts?.late_stops, stops: counts?.stops, voids: counts?.voids,
    outages: counts?.outages, overrides: counts?.overrides, forced: counts?.forced_ends, conflicts: counts?.conflicts, commands: counts?.failed_commands };

  const headerRight = (
    <button onClick={load} data-testid="kiosk-review-refresh" title="Refresh"
      style={{ background: 'none', border: '1px solid #2A2A2A', borderRadius: 8, padding: '10px 12px', color: GREY, cursor: 'pointer' }}>
      <RefreshCw className="h-4 w-4" />
    </button>
  );

  return (
    <SuperAdminShell title="Review" active="review" headerRight={headerRight}>
      <main style={{ padding: '20px 16px' }} data-testid="sports-kiosk-review-page">
        <p style={{ fontSize: 12, color: GREY, marginBottom: 14, maxWidth: 760, lineHeight: 1.5 }}>
          Every stop with a reason, every session not charged, every outage, override and forced end, every failed command and every
          double settlement — in time order. <b style={{ color: '#ccc' }}>Needs attention</b> = overrides, forced ends, outages, court comps above zero, conflicts.
        </p>

        {denied ? <AdminDeniedNotice /> : (
          <>
            <div className="flex items-center justify-between gap-3 flex-wrap mb-4">
              <PeriodPicker from={from} to={to} onChange={(f, t2) => setRange([f, t2])} testPrefix="kiosk-review" />
              <select value={kioskId} onChange={e => setKioskId(e.target.value)} data-testid="kiosk-review-device"
                style={{ ...inputStyle, width: 220, padding: '6px 8px', fontSize: 12 }}>
                <option value="">All devices</option>
                {devices.map(d => <option key={d.kiosk_id} value={d.kiosk_id}>{d.name}{d.site_name ? ` (${d.site_name})` : ''}</option>)}
              </select>
            </div>
            <div className="flex gap-2 mb-5 flex-wrap">
              {FILTERS.map(([k, label]) => {
                const on = filter === k;
                const c = k === 'conflicts' ? '#FF2D55' : (k === 'attention' || k === 'late') ? AMBER : RED;
                return (
                  <button key={k} onClick={() => setFilter(k)} data-testid={`kiosk-review-filter-${k}`}
                    style={{ fontSize: 12, fontWeight: 700, padding: '7px 14px', borderRadius: 999, cursor: 'pointer',
                      border: `1px solid ${on ? c : '#2A2A2A'}`, background: on ? c + '18' : 'transparent', color: on ? c : GREY }}>
                    {label}{countOf[k] != null ? <span style={{ opacity: 0.7 }}> {countOf[k]}</span> : null}
                  </button>
                );
              })}
            </div>

            {loading && !data ? (
              <p style={{ color: GREY, textAlign: 'center', padding: 40 }}>Loading…</p>
            ) : items.length === 0 ? (
              <div style={{ ...card, textAlign: 'center', padding: 50 }} data-testid="kiosk-review-empty">
                <ShieldAlert className="h-10 w-10 mx-auto mb-3" style={{ color: '#333' }} />
                <p style={{ color: GREY, fontSize: 14 }}>{filter === 'attention' ? 'Nothing needs attention in this period.' : 'Nothing to review here.'}</p>
              </div>
            ) : (
              <div data-testid="kiosk-review-list">{items.map((it, i) => <Item key={`${it.kind}-${it.row_id || it.command_id}-${i}`} it={it} />)}</div>
            )}

            {/* Scorecard */}
            {score && (
              <div style={{ marginTop: 28 }} data-testid="kiosk-scorecard">
                <div className="flex items-baseline justify-between flex-wrap gap-2 mb-3">
                  <h2 style={{ fontFamily: 'Oswald, sans-serif', fontSize: 16, fontWeight: 800, textTransform: 'uppercase', letterSpacing: 0.5 }}>Per-device scorecard</h2>
                  <span style={{ fontSize: 11, color: GREY }}>{score.period.from} to {score.period.to} · fleet stop average {score.fleet_stop_pct_avg}% · derived</span>
                </div>
                <div style={{ background: '#141414', border: '1px solid #1E1E1E', borderRadius: 12, overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 980 }}>
                    <thead>
                      <tr style={{ borderBottom: '1px solid #1E1E1E' }}>
                        {['Device', 'Sessions', 'Stops', 'Free stops', 'Mistakes / day', 'Outages / day', 'Late stops', 'Retained by court', 'Ran after a problem (15 min)'].map((h, i) => (
                          <th key={h} style={{ ...labelStyle, textAlign: i === 0 ? 'left' : 'right', padding: '10px 12px', fontWeight: 700 }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>{score.devices.map(c => <Scorecard key={c.kiosk_id} card={c} />)}</tbody>
                  </table>
                </div>
                <p style={{ fontSize: 11, color: '#555', marginTop: 8, lineHeight: 1.5 }}>
                  Free stops = stops that charged nothing. ⚠ on Stops = more than twice the fleet average (5+ sessions). Late stops = problem stops at minute 45–59 that
                  dodged the one-hour minimum (the amount is what went uncharged). The last column counts a problem or outage stop
                  followed by a new session on the same phone within 15 minutes — the machine ran fine after a &quot;problem&quot;.
                </p>
              </div>
            )}
          </>
        )}
      </main>
    </SuperAdminShell>
  );
}
